import { useState, useEffect } from "react";
import api from "../api/axios";
import {
  Box,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Typography,
  Chip,
  Switch,
  Tooltip,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import FormModal from "../components/FormModal";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNotify } from "../context/NotificationContext";

export default function PricingPlans() {
  const { notify } = useNotify();
  const [plans, setPlans] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const [name, setName] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [price, setPrice] = useState("");
  const [features, setFeatures] = useState([""]);
  const [isPopular, setIsPopular] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);

  const confirmDelete = async () => {
    await api.delete(`/plans/${planToDelete.id}`);
    setConfirmOpen(false);
    setPlanToDelete(null);
    notify("Pricing plan deleted successfully");
    loadPlans();
  };

  const loadPlans = async () => {
    const res = await api.get("/plans");
    setPlans(res.data);
  };

  const loadServices = async () => {
    const res = await api.get("/services");
    setServices(res.data);
  };

  useEffect(() => {
    loadPlans();
    loadServices();
  }, []);

  const plansForService = serviceId
    ? plans.filter((p) => p.service_id === serviceId)
    : [];

  const openModal = (plan = null) => {
    setCurrent(plan);
    setName(plan?.name || "");
    setServiceId(plan?.service_id || "");
    setPrice(plan?.price || "");
    setFeatures(plan?.features?.map((f) => f.feature) || [""]);
    setIsPopular(plan?.is_popular || false);
    setOpen(true);
  };

  const save = async () => {
    if (!serviceId) return notify("Select a service", "error");

    const count = plans.filter((p) => p.service_id === serviceId).length;

    if (!current && count >= 3) {
      notify("Maximum 3 plans allowed per service", "error");
      return;
    }

    const payload = {
      name,
      service_id: serviceId,
      price,
      is_popular: count === 0 ? true : isPopular,
      features: features.filter((f) => f.trim() !== ""),
    };

    if (current) {
      await api.put(`/plans/${current.id}`, payload);
    } else {
      await api.post("/plans", payload);
    }

    setOpen(false);
    loadPlans();
    if (current) {
      notify("Pricing plan updated successfully");
    } else {
      notify("Pricing plan saved successfully");
    }
  };

  const remove = async (plan) => {
    const count = plans.filter((p) => p.service_id === plan.service_id).length;

    if (count <= 1) {
      notify("At least one plan is required per service", "error");
      return;
    }
    setPlanToDelete(plan);
    setConfirmOpen(true);
  };

  const handleFeatureChange = (i, v) => {
    const f = [...features];
    f[i] = v;
    setFeatures(f);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Pricing Plans
        </Typography>
        <Typography color="text.secondary">
          Manage service pricing & features
        </Typography>
      </Box>

      <Button variant="contained" onClick={() => openModal()} sx={{ mb: 2 }}>
        Add Pricing Plan
      </Button>

      {/* Table */}
      <Table>
        <TableHead sx={{ background: "#111827", borderRadius: 2 }}>
          <TableRow>
            <TableCell sx={{ color: "#ffffff" }}>Name</TableCell>
            <TableCell sx={{ color: "#ffffff" }}>Service</TableCell>
            <TableCell sx={{ color: "#ffffff" }}>Price</TableCell>
            <TableCell sx={{ color: "#ffffff" }}>Features</TableCell>
            <TableCell sx={{ color: "#ffffff" }}>Popular</TableCell>
            <TableCell width="120" sx={{ color: "#ffffff" }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {plans.map((p) => {
            const servicePlansCount = plans.filter(
              (x) => x.service_id === p.service_id,
            ).length;

            return (
              <TableRow key={p.id} hover>
                <TableCell>{p.name}</TableCell>
                <TableCell>{p.service?.name}</TableCell>
                <TableCell>£{p.price}</TableCell>
                <TableCell>
                  {p.features?.length ? (
                    p.features.map((f, i) => (
                      <Chip
                        key={i}
                        label={f.feature}
                        size="small"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))
                  ) : (
                    <Typography color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {p.is_popular ? (
                    <Chip
                      icon={<StarIcon />}
                      label="Popular"
                      color="primary"
                      size="small"
                    />
                  ) : (
                    <Typography color="text.secondary">—</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => openModal(p)}>
                    <EditIcon />
                  </IconButton>

                  <Tooltip
                    title={
                      servicePlansCount <= 1
                        ? "At least one plan is required"
                        : "Delete plan"
                    }
                  >
                    <span>
                      <IconButton
                        color="error"
                        disabled={servicePlansCount <= 1}
                        onClick={() => remove(p)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Modal */}
      <FormModal
        open={open}
        title={current ? "Edit Pricing Plan" : "Add Pricing Plan"}
        onClose={() => setOpen(false)}
        onSubmit={save}
      >
        <TextField
          fullWidth
          label="Plan Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
        />

        <TextField
          select
          fullWidth
          label="Service"
          value={serviceId}
          onChange={(e) => setServiceId(e.target.value)}
          sx={{ mb: 2 }}
          disabled={current}
        >
          {services.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>

        {plansForService.length >= 3 && !current && (
          <Typography color="error" variant="caption">
            Maximum 3 plans allowed for this service
          </Typography>
        )}

        <TextField
          fullWidth
          type="number"
          label="Price"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Divider sx={{ my: 2 }} />

        {/* Popular Toggle */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography sx={{ flexGrow: 1 }}>Mark as Popular</Typography>
          <Switch
            checked={plansForService.length === 1 ? true : isPopular}
            disabled={plansForService.length === 1}
            onChange={(e) => setIsPopular(e.target.checked)}
          />
        </Box>

        {/* Features */}
        <Typography fontWeight="bold">Features</Typography>

        {features.map((f, i) => (
          <Box key={i} sx={{ display: "flex", gap: 1, mt: 1 }}>
            <TextField
              fullWidth
              value={f}
              placeholder={`Feature ${i + 1}`}
              onChange={(e) => handleFeatureChange(i, e.target.value)}
            />
            <Button
              color="error"
              disabled={features.length === 1}
              onClick={() => setFeatures(features.filter((_, x) => x !== i))}
            >
              Remove
            </Button>
          </Box>
        ))}

        <Button onClick={() => setFeatures([...features, ""])} sx={{ mt: 1 }}>
          Add Feature
        </Button>
      </FormModal>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        PaperProps={{
          sx: {
            background: "#111827",
            color: "#fff",
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle>Delete Pricing Plan</DialogTitle>

        <DialogContent>
          Are you sure you want to delete
          <strong> {planToDelete?.name}</strong>?
          <br />
          This action cannot be undone.
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
